import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, combineLatest, from, map, mergeMap, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  constructor(private httpService: HttpClient) {}

  /**
   * Normalizes Promise-based error handling used by legacy async flows.
   * @param error Error object returned from HTTP operations.
   */
  handleError(error: any): Promise<any> {
    if (error.error instanceof Blob) {
      return error.error.text().then((text: string) => {
        try {
          const json = JSON.parse(text);
          return Promise.reject(json.message || json.ErrorMessage || text);
        } catch {
          return Promise.reject(text || 'An unknown error occurred');
        }
      });
    }
    if (error.status === 200) {
      return Promise.resolve('No data');
    }
    if (error.status === 404) {
      return Promise.resolve('No data found');
    }
    return Promise.reject((error?.error?.message ?? error.error) || 'An unknown error occurred');
  }

  /**
   * Extracts standard API payload from `response` wrapper when present.
   * @param payload Raw API payload.
   */
  private extractResponse<T = any>(payload: any): T {
    if (payload && Object.prototype.hasOwnProperty.call(payload, 'response')) {
      return payload.response as T;
    }
    return payload as T;
  }

  /**
   * Converts Blob error payloads to message text.
   * @param blob Error Blob returned by API.
   */
  private parseBlobError(blob: Blob): Observable<never> {
    return from(blob.text()).pipe(
      mergeMap((text: string) => {
        try {
          const json = JSON.parse(text);
            const message = json.message || json.ErrorMessage || text;
          return throwError(() => message);
        } catch {
          return throwError(() => text || 'An unknown error occurred');
        }
      })
    );
  }

  /**
   * Maps HttpErrorResponse to user-meaningful error messages.
   * @param error HTTP error response.
   */
  private logAndHandleError(error: HttpErrorResponse): Observable<string | never> {
    if (error.error instanceof Blob) {
      return this.parseBlobError(error.error);
    }
    if (error.status === 0) {
      return throwError(() => 'Network or protocol error. Please check your connection or try again later.');
    }
    if (error?.status === 403 && error?.error?.ErrorMessage) {
      return throwError(() => error.error.ErrorMessage);
    }
    if (error?.status === 500) {
      return throwError(() => 'Internal Server Error');
    }
    return throwError(() => (error?.error?.message ?? error.error) || 'An unknown error occurred');
  }

  /**
   * Performs HTTP GET and unwraps API response wrapper when present.
   * @param url Endpoint URL.
   */
  httpGet(url: string) {
    return this.httpService.get<any[]>(url).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Performs HTTP GET with optional query params and unwraps response wrapper.
   * @param url Endpoint URL.
   * @param params Optional query params object.
   */
  httpGetWithHeaders(url: string, params: any = null) {
    return this.httpService.get<any[]>(url, params ? { params: params } : {}).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Performs HTTP GET expecting Blob response content.
   * @param url Endpoint URL.
   */
  httpGetBlob(url: string) {
    return this.httpService.get(url, { responseType: 'blob' as 'json' }).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Performs HTTP POST and unwraps response wrapper.
   * @param url Endpoint URL.
   * @param payload Request body payload.
   */
  httpPost(url: string, payload: any) {
    return this.httpService.post(url, payload).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Performs HTTP POST with optional request options and unwraps response wrapper.
   * @param url Endpoint URL.
   * @param payload Request body payload.
   * @param options Optional HttpClient options.
   */
  httpPostWithOptions(url: string, payload: any, options?: any) {
    return this.httpService.post(url, payload, options ? options : {}).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Uploads a file and emits progress/status events.
   * @param url Endpoint URL.
   * @param file File payload to upload.
   */
  httpFilePost(url: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpService.post(url, formData, { reportProgress: true, observe: 'events' }).pipe(
      map((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const total = event.total || 1;
            const progress = Math.round((100 * event.loaded) / total);
            return { status: 'progress', message: progress };
          case HttpEventType.Response:
            return event.body;
          default:
            return `Unhandled event: ${event.type}`;
        }
      }),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Performs HTTP PUT and unwraps response wrapper.
   * @param url Endpoint URL.
   * @param payload Request body payload.
   */
  httpPut(url: string, payload: any) {
    return this.httpService.put(url, payload).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Performs HTTP PATCH and unwraps response wrapper.
   * @param url Endpoint URL.
   * @param payload Request body payload.
   */
  httpPatch(url: string, payload: any) {
    return this.httpService.patch(url, payload).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Fetches multiple URLs in parallel and unwraps each response wrapper.
   * @param urls List of endpoint URLs.
   */
  httpCombineLatest(urls: string[]) {
    const httpUrls = urls.map((url) => this.httpService.get(url));
    return combineLatest(httpUrls).pipe(
      map((res: any) => res.map((item) => this.extractResponse(item))),
      catchError(this.logAndHandleError.bind(this))
    );
  }

  /**
   * Performs HTTP DELETE and unwraps response wrapper.
   * @param url Endpoint URL.
   * @param options Optional HttpClient options.
   */
  httpDelete(url: string, options?: any) {
    return this.httpService.delete(url, options).pipe(
      map((res: any) => this.extractResponse(res)),
      catchError(this.logAndHandleError.bind(this))
    );
  }
}
